import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import TradeSelection from './components/TradeSelection';
import ProviderDetailsForm from './components/ProviderDetailsForm';
import SubscriptionPayment from './components/SubscriptionPayment';
import OnfidoVerification from './components/OnfidoVerification';
import ApplicationReview from './components/ApplicationReview';
import WorkwearPurchase from './components/WorkwearPurchase';
import OnboardingComplete from './components/OnboardingComplete';
import EarningsCalculator from './components/EarningsCalculator';
import LaunchScheduleCards from './components/LaunchScheduleCards';

export default function ProviderSignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [providerData, setProviderData] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleProviderSignIn = () => {
    navigate('/provider-login');
  };

  // Trade display names mapping
  const tradeDisplayNames = {
    'handyman': 'Handyman Services',
    'plumbing': 'Plumbing Services',
    'electrical': 'Electrical Services',
    'cleaning': 'Cleaning Services',
    'transportation': 'Transportation & Moving',
    'waste': 'Waste Removal'
  };

  const faqs = [
    {
      question: "How do I become a Taskbudy provider?",
      answer: "Choose your trade, complete the application, pay the annual subscription, then verify your details. Once approved, you can start accepting jobs."
    },
    {
      question: "How much does it cost to join?",
      answer: "The first year costs £378 inc. VAT (25% discount). After that, it renews at £35/month (£420 + VAT per year)."
    },
    {
      question: "Do I get a refund if I'm not approved?",
      answer: "Yes. If your application isn't approved, your subscription is fully refunded."
    },
    {
      question: "Do I need public liability insurance?",
      answer: "Yes. Public liability insurance is required for all trades and must be uploaded during verification."
    },
    {
      question: "What documents will I need?",
      answer: "Documents depend on your trade and may include qualifications, licences, or vehicle documents. All required uploads are shown in your dashboard."
    },
    {
      question: "How does identity verification work?",
      answer: "After payment, you'll complete a secure identity check using Onfido, a trusted third-party provider."
    },
    {
      question: "When can I start receiving jobs?",
      answer: "Once your documents are approved and you've purchased the Taskbudy workwear kit, your account becomes active and jobs appear in your dashboard."
    },
    {
      question: "Will I see pay rates before paying?",
      answer: "Yes. During your application, you'll see your city tier, typical pay rates, and any after-hours or call-out fees before payment."
    },
    {
      question: "Am I employed by Taskbudy?",
      answer: "No. Providers are self-employed independent contractors and manage their own taxes."
    },
    {
      question: "How do I get paid?",
      answer: "Providers are paid weekly, with all payments handled through the platform."
    },
    {
      question: "Can I choose when I work?",
      answer: "Yes. You choose your cities, availability, and which jobs to accept."
    }
  ];

  useEffect(() => {
    const step = searchParams.get('step');
    if (step) {
      setCurrentStep(parseInt(step));
    }
  }, [searchParams]);

  // Scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTradeSelect = (tradeId) => {
    setSelectedTrade(tradeId);
    setCurrentStep(2);
  };

  const handleProviderDetailsSubmit = async (data: any) => {
    console.log('Provider details submitted:', data);
    
    // Store the form data including photo URL
    const completeData = {
      ...providerData,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      password: data.password,
      postcode: data.postcode,
      city: data.city,
      profilePhotoUrl: data.profilePhotoUrl || data.selfieUrl,
      selfieUrl: data.selfieUrl || data.profilePhotoUrl,
      selectedTrade: selectedTrade,
    };

    setProviderData(completeData);

    console.log('✅ Photo URL saved:', data.profilePhotoUrl || data.selfieUrl);
    console.log('✅ Navigating with complete data:', completeData);

    // Navigate to subscription payment WITH ALL DATA
    navigate('/provider-apply-payment', {
      state: completeData
    });
  };

  const handleSubscriptionComplete = () => {
    setCurrentStep(4);
  };

  const handleVerificationComplete = () => {
    setCurrentStep(5);
  };

  const handleVerificationSkip = () => {
    setCurrentStep(5);
  };

  const handleDocumentsComplete = () => {
    setCurrentStep(6);
  };

  const handleWorkwearComplete = () => {
    setCurrentStep(7);
  };

  const handleWorkwearSkip = () => {
    setCurrentStep(7);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      navigate('/provider-apply');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/50c2c99096fa15debe1a61e296d6044b.png" 
                alt="Taskbudy" 
                className="h-10"
              />
            </a>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                Home
              </a>
              <a href="/services" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                Services
              </a>
              <a href="/locations" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                Locations
              </a>
              <a href="/pricing" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                Pricing
              </a>
              <a href="/about" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                About
              </a>
              <a href="/contact" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                Contact
              </a>
              <a href="/auth/signin" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                Sign In
              </a>
              <a href="/auth/signup" className="text-gray-700 hover:text-[#fe7a14] font-medium transition-colors cursor-pointer">
                Sign Up
              </a>
              <button
                onClick={handleProviderSignIn}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap"
              >
                Provider Sign In
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-[#203163] w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg border-t border-gray-200">
            <div className="flex flex-col py-2">
              <a
                href="/"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                Home
              </a>
              <a
                href="/services"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                Services
              </a>
              <a
                href="/locations"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                Locations
              </a>
              <a
                href="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                Pricing
              </a>
              <a
                href="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                About
              </a>
              <a
                href="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                Contact
              </a>
              <div className="border-t border-gray-200 my-2"></div>
              <a
                href="/auth/signin"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                Sign In
              </a>
              <a
                href="/auth/signup"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-colors cursor-pointer"
              >
                Sign Up
              </a>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleProviderSignIn();
                }}
                className="mx-4 my-2 px-4 py-3 bg-teal-600 text-white font-semibold rounded-lg text-center hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap"
              >
                Provider Sign In
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Add padding to account for fixed navbar */}
      <div className="h-16"></div>

      {/* === Hero Section === */}
      {currentStep === 1 && (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/d54a6d3c06c7ace75f43529565ff1ac5.jpg"
              alt="Professional tradespeople at work"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0B3C5D]/95 via-[#0B3C5D]/90 to-[#328BA8]/85"></div>
          </div>

          {/* Animated Accent Elements */}
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-1/4 left-10 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-[#328BA8] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {/* Content */}
              <div className="text-white text-center">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight px-4">
                  Trade Jobs Sent to You – No Bidding
                </h1>

                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-3 sm:mb-4 leading-relaxed max-w-3xl mx-auto px-4">
                  Uber-style dispatch. Jobs are sent to nearby providers. First to accept gets the job.
                </p>

                <p className="text-sm sm:text-base md:text-lg text-white/90 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto px-4">
                  Customer pays in-app. You get paid automatically.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 px-4">
                  <a
                    href="#trade-selection"
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-[#328BA8] text-white rounded-lg font-semibold hover:bg-[#328BA8]/90 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer transform hover:scale-105 text-sm sm:text-base"
                  >
                    Join as a Provider
                  </a>
                  <a
                    href="#how-it-works"
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg hover:bg-white/20 hover:border-white/50 transition-all duration-300 font-semibold whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
                  >
                    See How It Works
                  </a>
                </div>

                {/* Benefits Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8 max-w-4xl mx-auto px-4">
                  <div className="flex flex-col items-center space-y-2 bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <i className="ri-shield-check-line text-white text-xl sm:text-2xl"></i>
                    </div>
                    <div className="text-center">
                      <h3 className="font-semibold text-white mb-1 text-xs sm:text-sm">Verified Badge</h3>
                      <p className="text-[10px] sm:text-xs text-white/80">Stand out with trust</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center space-y-2 bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <i className="ri-time-line text-white text-xl sm:text-2xl"></i>
                    </div>
                    <div className="text-center">
                      <h3 className="font-semibold text-white mb-1 text-xs sm:text-sm">Flexible Hours</h3>
                      <p className="text-[10px] sm:text-xs text-white/80">Work when you want</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center space-y-2 bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <i className="ri-money-pound-circle-line text-white text-xl sm:text-2xl"></i>
                    </div>
                    <div className="text-center">
                      <h3 className="font-semibold text-white mb-1 text-xs sm:text-sm">Weekly Payouts</h3>
                      <p className="text-[10px] sm:text-xs text-white/80">Get paid fast</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center space-y-2 bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <i className="ri-customer-service-2-line text-white text-xl sm:text-2xl"></i>
                    </div>
                    <div className="text-center">
                      <h3 className="font-semibold text-white mb-1 text-xs sm:text-sm">24/7 Support</h3>
                      <p className="text-[10px] sm:text-xs text-white/80">We're here to help</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex flex-wrap justify-center gap-6 sm:gap-8 pt-4 sm:pt-6 border-t border-white/20 px-4">
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-bold text-white mb-2">
                      85%
                    </div>
                    <div className="text-white/90 font-medium text-sm sm:text-base">
                      You Keep
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-bold text-white mb-2">
                      15%
                    </div>
                    <div className="text-white/90 font-medium text-sm sm:text-base">
                      We Keep Commission
                    </div>
                  </div>
                  <div className="text-center max-w-xs">
                    <div className="text-white/80 text-xs sm:text-sm italic">
                      On all completed jobs
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Progress Bar */}
      {currentStep !== 1 && currentStep < 7 && (
        <div className="bg-white border-b border-gray-200 sticky top-[64px] z-10">
          <div className="max-w-7xl mx-auto px-4 py-4">
            {/* Trade Badge - Show when trade is selected */}
            {selectedTrade && (
              <div className="flex items-center justify-center mb-3">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0B3C5D] to-[#328BA8] text-white px-4 py-2 rounded-full shadow-md">
                  <i className="ri-briefcase-line text-lg"></i>
                  <span className="font-semibold text-sm">
                    Applying for: {tradeDisplayNames[selectedTrade] || selectedTrade}
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">
                Step {currentStep} of 5
              </div>
              <div className="text-sm font-medium text-gray-600">
                {Math.round((currentStep / 5) * 100)}% Complete
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-[#0B3C5D] to-[#328BA8] h-2 rounded-full transition-all duration-500"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <i className="ri-error-warning-line text-xl"></i>
              <span>{error}</span>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <>
            {/* How Becoming a Taskbudy Provider Works Section */}
            <div id="how-it-works" className="max-w-4xl mx-auto mb-12 px-4">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">How becoming a Taskbudy provider works</h2>
              
              <p className="text-lg text-gray-700 mb-4">
                Taskbudy is a vetted platform built for professional, self-employed tradespeople.
                <br />
                To protect both providers and customers, every application follows a clear approval process.
              </p>

              <div className="space-y-8 mt-8">
                {/* Step 1 */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Step 1 — Apply & choose your trade</h3>
                  <p className="text-gray-700 mb-3">
                    Select the service you want to offer and complete the application with your details, cities, and profile photo.
                  </p>
                  <p className="text-gray-700 mb-2">During your application, you'll see:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Your city tier</li>
                    <li>Typical pay rates for normal hours and after-hours</li>
                    <li>Any applicable late call-out fees</li>
                  </ul>
                  <p className="text-gray-700 mt-3">You'll know what to expect before moving forward.</p>
                </div>

                {/* Step 2 */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Step 2 — Annual subscription (pending approval)</h3>
                  <p className="text-gray-700 mb-3">
                    Once you submit your application, you'll be asked to pay the annual provider subscription.
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-3">
                    <li><strong>£378 inc VAT</strong> for your first year (25% discount)</li>
                    <li>Then <strong>£35/month</strong> (£420 + VAT per year)</li>
                  </ul>
                  <p className="text-gray-700 mb-2">This payment:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Unlocks the verification process</li>
                    <li>Is pending approval</li>
                    <li>Is fully refundable if you're not approved</li>
                  </ul>
                  <p className="text-gray-700 mt-3">You won't receive jobs at this stage.</p>
                </div>

                {/* Step 3 */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Step 3 — Verify your details</h3>
                  <p className="text-gray-700 mb-3">
                    After payment, you'll access your provider dashboard with a <strong>Verification Pending</strong> status.
                  </p>
                  <p className="text-gray-700 mb-2">You'll be asked to:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Complete identity verification via Onfido</li>
                    <li>Upload required documents for your trade<br />
                      <span className="text-sm">(public liability insurance is mandatory for all trades)</span>
                    </li>
                  </ul>
                </div>

                {/* Step 4 */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Step 4 — Review & approval</h3>
                  <p className="text-gray-700 mb-3">Our team reviews your application and documents.</p>
                  <p className="text-gray-700 mb-2">If approved, you'll be prompted to:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-3">
                    <li>Purchase your Taskbudy workwear kit</li>
                    <li>Once completed, your account becomes active</li>
                  </ul>
                  <p className="text-gray-700 mb-2">If your application isn't approved:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Your subscription is fully refunded</li>
                    <li>You'll be notified with a clear outcome</li>
                  </ul>
                </div>

                {/* Step 5 */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Step 5 — Go live & start earning</h3>
                  <p className="text-gray-700 mb-2">Once active, you can:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>See and accept jobs in your selected cities</li>
                    <li>Get paid weekly</li>
                    <li>Work when it suits you, with upfront pricing and no quoting</li>
                  </ul>
                </div>

                {/* Reassurance */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Reassurance</h3>
                  <p className="text-gray-700">
                    Taskbudy is transparent and fair.
                    <br />
                    You'll always see key information before paying, and you'll never go live without approval.
                  </p>
                </div>
              </div>
            </div>

            {/* Launch Schedule Cards */}
            <div className="py-12 -mx-4">
              <LaunchScheduleCards />
            </div>

            <div id="trade-selection">
              <TradeSelection
                selectedTrade={selectedTrade}
                onSelectTrade={handleTradeSelect}
              />
            </div>

            {/* Earnings Calculator */}
            <EarningsCalculator />

            {/* Provider FAQs Section */}
            <div className="max-w-4xl mx-auto mt-16 px-4 pb-12">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Provider FAQs</h2>
                <p className="text-lg text-gray-600">Common questions about becoming a Taskbudy provider</p>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left cursor-pointer"
                    >
                      <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
                      <i
                        className={`ri-arrow-down-s-line text-2xl text-gray-500 transition-transform flex-shrink-0 ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Still Have Questions CTA */}
              <div className="mt-10 text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Still have questions?</h3>
                <p className="text-gray-600 mb-4">Our team is here to help you get started</p>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-customer-service-2-line text-xl"></i>
                  Contact Support
                </a>
              </div>
            </div>
          </>
        )}

        {currentStep === 2 && (
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0B3C5D] to-[#328BA8] rounded-full flex items-center justify-center shrink-0">
                  <i className="ri-user-add-line text-white text-3xl"></i>
                </div>
                <div className="text-left">
                  <h2 className="text-3xl font-bold text-gray-900 mb-1">Your Details</h2>
                  <p className="text-gray-600">Tell us about yourself</p>
                </div>
              </div>
            </div>
            <ProviderDetailsForm
              selectedTrade={selectedTrade}
              onSubmit={handleProviderDetailsSubmit}
              onBack={handleBack}
            />
          </div>
        )}

        {currentStep === 3 && (
          <SubscriptionPayment
            selectedTrade={selectedTrade}
            providerId={providerData.profileId}
            onNext={handleSubscriptionComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === 4 && (
          <OnfidoVerification
            userId={providerData.userId}
            profileId={providerData.profileId}
            onComplete={handleVerificationComplete}
            onSkip={handleVerificationSkip}
          />
        )}

        {currentStep === 5 && (
          <ApplicationReview
            formData={{ ...formData, trade: selectedTrade }}
            onNext={handleDocumentsComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === 6 && (
          <WorkwearPurchase
            userId={providerData.userId}
            profileId={providerData.profileId}
            onNext={handleWorkwearComplete}
            onSkip={handleWorkwearSkip}
          />
        )}

        {currentStep === 7 && (
          <OnboardingComplete
            subscriptionPaid={true}
            workwearPurchased={true}
            documentsUploaded={true}
          />
        )}
      </div>
    </div>
  );
}
